/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { createObservableDataAction, IAction, IActionContext, sendRequest } from '@msdyn365-commerce/core';
import { SubmitUserReviewInput } from './inputs/submit-user-review-input';

/**
 * This data action allows submitting review to the reviews service
 * @param input This user review submission text
 * @param ctx The data action context
 */
async function submitReview(input:SubmitUserReviewInput, ctx: IActionContext):Promise<string> {
    if (!inputIsValid(input)) {
        ctx.trace(`[SubmitUserReview] Failed to submit reviews for product due to bad input ${input}`);
        return '';
    }

    const requestUrl = `${input.serviceEndpoint}/v2.0/reviews/product/${input.productId}/user?` +
        `tenantId=${input.tenantId}&` +
        `channelId=${input.channelId}&` +
        `locale=${input.locale}`;

    return sendRequest<string>(requestUrl, 'post', input.userReviewData, {headers: {Authorization: `id_token ${input.authorization}`}}).then((response) => {
        if (response.status !== 200 && response.status !== 201) {
            ctx.trace(`[SubmitUserReview] Failed to submit reviews for product`);
            ctx.trace(response.data);
            return '';
        }
        return  response.data;
    }).catch((error) => {
        ctx.trace(`[SubmitUserReview] Failed to submit reviews for product`);
        ctx.telemetry.exception(error);
        ctx.telemetry.debug(`[SubmitUserReview] Failed to submit reviews for product`);
        ctx.trace(error);
        return '';
    });
}

function inputIsValid(input: SubmitUserReviewInput): Boolean {
    return input && !!input.productId && !!input.tenantId;
}

export const submitReviewDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/ratings-reviews/submit-user-review',
    action: <IAction<string>>submitReview
});

export default submitReviewDataAction;
